import orgSlice from "./orgs"
import tagSlice from "./tags"
import mapSlice from "./map"
import projectSlice from "./projects"
import droneSlice from "./drones"
import entitySlice from "./entities"
import licensesSlice from "./licenses"
import permissionsSlice from "./permissions"
import ingestSlice from "./ingest"
import userLocationsSlice from "./user-locations"
import notificationsSlice from "./notifications"
import orgUserLocationsSlice from "./org-user-locations"

import { Socket } from "socket.io-client"
import { Handler, SocketHandler, SocketSlice, SocketStore } from "./types"
import { AnyAction } from "@nova/constants"
import { ActionCreatorWithPayload, AsyncThunk, PayloadAction } from "@reduxjs/toolkit"

export class Builder {
    handlers: Record<string, Handler[]> = {}

    // overload for an action-creator-with-payload
    addCase<Payload>(
        actionCreator: ActionCreatorWithPayload<Payload>,
        fn: (store: SocketStore, action: PayloadAction<Payload>, socket: Socket) => void
    ): this

    // overload for a thunk fulfilled action
    addCase<Returned, ThunkArg, RejectedValue>(
        actionCreator: AsyncThunk<Returned, ThunkArg, { rejectValue: RejectedValue }>["fulfilled"],
        fn: (store: SocketStore, action: PayloadAction<Returned>, socket: Socket) => void
    ): this

    // overload for a thunk pending action
    addCase<ThunkArg>(
        actionCreator: AsyncThunk<undefined, ThunkArg, object>["pending"],
        fn: (
            store: SocketStore,
            action: PayloadAction<undefined, string, { arg: ThunkArg; requestId: string }>,
            socket: Socket
        ) => void
    ): this

    // the single implementation signature (not visible externally)
    addCase(
        actionCreator: AnyAction,
        fn: (store: SocketStore, action: AnyAction, socket: Socket) => void
    ): this {
        this.handlers[actionCreator.type] ??= []
        this.handlers[actionCreator.type].push(fn)
        return this
    }

    addMatcher = (
        actions: AnyAction[],
        fn: (store: SocketStore, action: AnyAction, socket: Socket) => void
    ) => {
        actions.forEach((action) => {
            this.handlers[action.type] ??= []
            this.handlers[action.type].push(fn)
        })
        return this
    }
}

// combines socket "slices" and returns the handler that can be used for middleware
const combineSlices =
    (slices: SocketSlice[]) =>
    (store: SocketStore, socket: Socket): SocketHandler => {
        const builder = new Builder()
        slices.forEach((slice) => {
            Object.entries(slice.events || {}).forEach(([name, event]) => {
                socket.on(name, event(store, socket))
            })
            if (slice.handlers) {
                slice.handlers(builder)
            }
        })
        return (action: AnyAction) => {
            builder.handlers[action.type]?.forEach((handler) => {
                handler(store, action, socket)
            })
        }
    }

export const getSocketMiddleware = combineSlices([
    orgSlice,
    tagSlice,
    mapSlice,
    projectSlice,
    droneSlice,
    entitySlice,
    licensesSlice,
    permissionsSlice,
    ingestSlice,
    userLocationsSlice,
    notificationsSlice,
    orgUserLocationsSlice,
])
